import { HeroMinimal, BookingCTA, SectionHeader } from "../components/sections";
import {
  MessageSquare,
  Target,
  BarChart3,
  Clock,
  CheckCircle,
} from "lucide-react";

const measures = [
  {
    title: "Qualified conversations",
    description:
      "Replies and meetings from people who match your target profile, with context for your sales team.",
    icon: MessageSquare,
  },
  {
    title: "Sales-accepted opportunities",
    description:
      "Track which conversations become real opportunities, using qualification criteria agreed with your team.",
    icon: Target,
  },
  {
    title: "Pipeline contribution",
    description:
      "Understand the opportunities influenced by outbound, with attribution and context from your CRM.",
    icon: BarChart3,
  },
  {
    title: "Time returned to your team",
    description:
      "Review the manual tasks the system handles and where your team can spend more time selling.",
    icon: Clock,
  },
];
export default function ResultsPage() {
  return (
    <>
      <HeroMinimal
        title="Measure what moves your business forward."
        subtitle="A useful outbound system is measured by the conversations and opportunities it creates. We define success with your team and review the evidence together."
        ctaText="Book a Strategy Call"
        ctaHref="/book"
        secondaryCtaText="See How It Works"
        secondaryCtaHref="/how-it-works"
      />
      <section className="section" aria-labelledby="measurement-title">
        <div className="container">
          <SectionHeader
            id="measurement-title"
            eyebrow="Outcome focus"
            title="More context. Better decisions."
            description="Agree on the metrics that matter to your sales process. Review activity alongside business outcomes, rather than treating volume as success."
          />
          <div className="measurement-table-wrap">
            <table className="measurement-table">
              <caption className="visually-hidden">
                Business outcomes and what we measure
              </caption>
              <thead>
                <tr>
                  <th scope="col">Business outcome</th>
                  <th scope="col">What we measure together</th>
                </tr>
              </thead>
              <tbody>
                {measures.map((item) => (
                  <tr key={item.title}>
                    <th scope="row">
                      <div>
                        <item.icon
                          size={26}
                          className="text-accent"
                          aria-hidden="true"
                        />
                        <span>{item.title}</span>
                      </div>
                    </th>
                    <td>{item.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
      <section
        className="section bg-surface border-y border-border"
        aria-labelledby="review-title"
      >
        <div className="container editorial-grid">
          <SectionHeader
            id="review-title"
            eyebrow="A practical review process"
            title="Every result needs its context."
            description="A report should help you understand what happened, why it happened, and what to do next. We review the complete journey from prospect data to sales conversation."
          />
          <div
            className="work-shift review-flow"
            aria-label="The performance review journey"
          >
            {[
              "Target fit and data quality",
              "Messaging and response quality",
              "Sales handoff and qualification",
              "Pipeline progression and next experiments",
            ].map((item, index) => (
              <div className="work-shift-row" key={item}>
                <span className="review-node" aria-hidden="true">
                  0{index + 1}
                </span>
                <CheckCircle
                  size={20}
                  className="text-accent"
                  aria-hidden="true"
                />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="section" aria-labelledby="client-stories-title">
        <div className="container">
          <div className="editorial-grid">
            <SectionHeader
              id="client-stories-title"
              eyebrow="Client stories"
              title="Evidence you can evaluate."
              description="Public case studies will be shared here when verified outcomes and client permission are available."
            />
            <div className="card p-8">
              <h3 className="h3 mb-3">
                Discuss the approach for your business.
              </h3>
              <p className="body text-text-secondary">
                For now, explore the process or use a strategy call to discuss
                your market, the proposed system, and how we would measure
                progress.
              </p>
            </div>
          </div>
        </div>
      </section>
      <BookingCTA />
    </>
  );
}
